import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, type UIMessage, isToolOrDynamicToolUIPart, getToolName } from 'ai';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../../lib/api';
import ProductDetailPage from '../../products/templates/ProductDetailPage';

const API_BASE = import.meta.env.DEV ? '/api/v1' : 'https://api.jarad.beeapp.es/api/v1';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` });
const apiFetch = async (path: string, opts: RequestInit = {}) => {
  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...authHeaders(), ...opts.headers } });
  return res.json();
};

type CartItem = { productId?: string; name: string; variantSku: string; quantity: number; price: number; lineTotal: number; imageUrl?: string };
type CartState = { items: CartItem[]; subtotal: number; totalItems: number };
type ConvSummary = { id: string; title: string; updatedAt: string };

// ─── Product Drawer ───
const ProductDrawer = ({ productId, onClose }: { productId: string; onClose: () => void }) => (
  <>
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose} className="fixed inset-0 bg-black/40" style={{ zIndex: 60 }} />
    <motion.div
      initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      className="fixed right-0 bottom-0 w-full sm:w-[560px] bg-white shadow-2xl flex flex-col lg:top-0 top-16"
      style={{ zIndex: 61 }}>
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <h2 className="text-sm font-semibold">Detalle del producto</h2>
        <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {/* Force mobile layout: override lg/sm grid classes to single column */}
      <div className="flex-1 overflow-y-auto [&_.lg\:grid-cols-12]:!grid-cols-1 [&_.lg\:col-span-5]:!col-span-1 [&_.lg\:col-span-4]:!col-span-1 [&_.lg\:col-span-3]:!col-span-1 [&_.lg\:mt-0]:!mt-4 [&_.lg\:gap-6]:!gap-4 [&_.hidden.sm\:flex]:!flex [&_.sm\:hidden]:!block [&_.hidden.sm\:block]:!block">
        <ProductDetailPage productId={productId} />
      </div>
    </motion.div>
  </>
);

// ─── Main Page ───
const QuickOrderPage = () => {
  const { auth } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [cart, setCart] = useState<CartState>({ items: [], subtotal: 0, totalItems: 0 });
  const [cartLoading, setCartLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);
  const [updatingItem, setUpdatingItem] = useState<string | null>(null);
  const [drawerProductId, setDrawerProductId] = useState<string | null>(null);
  const [hasAcceptedAi, setHasAcceptedAi] = useState(
    typeof window === 'undefined' || !!localStorage.getItem('quick-order-ai-consent')
  );
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const acceptDisclaimer = () => {
    localStorage.setItem('quick-order-ai-consent', new Date().toISOString());
    setHasAcceptedAi(true);
    setShowDisclaimer(false);
  };

  // Conversations
  const [conversations, setConversations] = useState<ConvSummary[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [convsOpen, setConvsOpen] = useState(false);
  const [convsLoaded, setConvsLoaded] = useState(false);

  const welcomeMessage: UIMessage = {
    id: 'welcome',
    role: 'assistant',
    parts: [{ type: 'text' as const, text: `**Hola${auth.user?.name ? `, ${auth.user.name.split(' ')[0]}` : ''}!** Soy tu asistente de pedidos.\n\nDime qué necesitas:\n- "10 canario sin avena 1kg y 5 periquito 25kg"\n- "20 del 400526"\n- "Lo mismo que mi último pedido"` }]
  };

  // Load cart
  const loadCart = useCallback(async () => {
    try {
      const res = await api.cart.getCart();
      if (res.success && res.data) {
        const items = (res.data.items || []).map((i: any) => ({
          productId: i.productId, name: i.name, variantSku: i.variantSku,
          quantity: i.quantity, price: i.price, lineTotal: +(i.price * i.quantity).toFixed(2),
          imageUrl: i.imageUrl || ''
        }));
        setCart({ items, subtotal: res.data.subtotal || 0, totalItems: items.reduce((s: number, i: CartItem) => s + i.quantity, 0) });
      }
    } catch { /* */ }
    setCartLoading(false);
  }, []);

  useEffect(() => { loadCart(); }, [loadCart]);

  // Load conversations list
  const loadConversations = useCallback(async () => {
    try {
      const data = await apiFetch('/quick-order/conversations');
      if (data.success) setConversations(data.data || []);
    } catch { /* */ }
    setConvsLoaded(true);
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Cart actions
  const handleUpdateQty = async (productId: string, variantSku: string, newQty: number) => {
    if (newQty < 1) return handleRemoveItem(productId, variantSku);
    setUpdatingItem(variantSku);
    setCart(prev => {
      const items = prev.items.map(i =>
        i.productId === productId && i.variantSku === variantSku
          ? { ...i, quantity: newQty, lineTotal: +(i.price * newQty).toFixed(2) }
          : i
      );
      const subtotal = items.reduce((s, i) => s + i.lineTotal, 0);
      return { items, subtotal, totalItems: items.reduce((s, i) => s + i.quantity, 0) };
    });
    try { await api.cart.updateCartItem(productId, newQty, variantSku); } catch { await loadCart(); }
    setUpdatingItem(null);
  };

  const handleRemoveItem = async (productId: string, variantSku: string) => {
    setCart(prev => {
      const items = prev.items.filter(i => !(i.productId === productId && i.variantSku === variantSku));
      const subtotal = items.reduce((s, i) => s + i.lineTotal, 0);
      return { items, subtotal, totalItems: items.reduce((s, i) => s + i.quantity, 0) };
    });
    try { await api.cart.removeFromCart(productId, variantSku); } catch { await loadCart(); }
  };

  const { messages, sendMessage, status, error, setMessages } = useChat({
    transport: new DefaultChatTransport({
      api: `${API_BASE}/quick-order/chat`,
      headers: authHeaders,
      body: activeConvId ? { conversationId: activeConvId } : undefined,
    }),
    messages: [welcomeMessage],
  });

  const isLoading = status === 'streaming' || status === 'submitted';

  // Sync cart from SSE
  useEffect(() => {
    for (const msg of messages) {
      if (msg.role as string !== 'assistant') continue;
      for (const part of msg.parts) {
        if (!isToolOrDynamicToolUIPart(part) || (part as any).state !== 'output-available') continue;
        const r = (part as any).output as any;
        if (!r) continue;
        const cd = r.cart || (r.items !== undefined ? r : null);
        if (cd?.items) {
          setCart({
            items: cd.items.map((i: any) => ({ ...i, lineTotal: +(i.price * i.quantity).toFixed(2) })),
            subtotal: cd.subtotal || 0,
            totalItems: cd.totalItems || cd.items.reduce((s: number, i: any) => s + i.quantity, 0)
          });
        }
      }
    }
  }, [messages]);

  // Auto-scroll within chat container only
  useEffect(() => {
    const el = messagesEndRef.current;
    if (el) {
      const container = el.closest('[data-chat-scroll]');
      if (container) container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (hasAcceptedAi) inputRef.current?.focus();
  }, [hasAcceptedAi]);

  // Save conversation after AI finishes responding
  useEffect(() => {
    if (status !== 'ready' || messages.length <= 1) return;
    const save = async () => {
      try {
        if (activeConvId) {
          await apiFetch(`/quick-order/conversations/${activeConvId}`, { method: 'PUT', body: JSON.stringify({ messages }) });
        } else {
          // Create new conversation
          const data = await apiFetch('/quick-order/conversations', { method: 'POST', body: JSON.stringify({ messages }) });
          if (data.success && data.data?.id) {
            setActiveConvId(data.data.id);
            loadConversations();
          }
        }
      } catch { /* */ }
    };
    save();
  }, [status]);

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;
    setInputValue('');
    sendMessage({ text });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // Load a conversation
  const handleSelectConv = async (id: string) => {
    try {
      const data = await apiFetch(`/quick-order/conversations/${id}`);
      if (data.success && data.data?.messages) {
        setActiveConvId(id);
        setMessages(data.data.messages);
      }
    } catch { /* */ }
    setConvsOpen(false);
  };

  // New conversation
  const handleNewConv = () => {
    setActiveConvId(null);
    setMessages([welcomeMessage]);
    setConvsOpen(false);
  };

  const handleDeleteConv = async (id: string) => {
    try {
      await apiFetch(`/quick-order/conversations/${id}`, { method: 'DELETE' });
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeConvId === id) handleNewConv();
    } catch { /* */ }
  };

  const getTextContent = (msg: UIMessage): string =>
    msg.parts.filter((p): p is { type: 'text'; text: string } => p.type === 'text').map(p => p.text).join('');

  const renderToolParts = (msg: UIMessage) => {
    const parts = msg.parts.filter(p => isToolOrDynamicToolUIPart(p) && (p as any).state === 'output-available');
    if (!parts.length) return null;
    return parts.map((part, i) => {
      const r = (part as any).output as any;
      if (!r) return null;
      const tn = getToolName(part as any);
      if (tn === 'addToCart' && r.success && r.added) return (
        <motion.div key={`t-${i}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 text-green-700 bg-green-50/80 px-2.5 py-1 rounded-lg mt-1 text-xs">
          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
          {r.added.quantity}x {r.added.variant} — {(r.added.quantity * r.added.unitPrice).toFixed(2)}&euro;
          {r.adjusted && <span className="text-orange-600 ml-1">({r.adjusted})</span>}
        </motion.div>
      );
      if (tn === 'addToCart' && !r.success) return <div key={`t-${i}`} className="text-xs text-red-600 bg-red-50 px-2.5 py-1 rounded-lg mt-1">{r.error}</div>;
      if (tn === 'removeFromCart' && r.success) return <motion.div key={`t-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-gray-500 mt-1">Eliminado del carrito</motion.div>;
      if (tn === 'updateCartQuantity' && r.success) return <motion.div key={`t-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-blue-600 mt-1">Cantidad actualizada</motion.div>;
      return null;
    });
  };

  return (
    <div className="flex flex-col bg-gray-50" style={{ height: 'calc(100vh - 64px)' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={() => setConvsOpen(!convsOpen)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Conversaciones">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </button>
          <div className="w-7 h-7 bg-black rounded-full flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <div>
            <h1 className="text-sm font-semibold text-gray-900">Pedido rápido</h1>
            <p className="text-[11px] text-gray-500">Asistente IA</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleNewConv} className="text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50">Nueva</button>
          <button onClick={() => setCartOpen(!cartOpen)}
            className="relative flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-lg text-xs font-medium hover:bg-gray-800">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {cart.totalItems > 0 && <span className="bg-white text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{cart.totalItems > 99 ? '99' : cart.totalItems}</span>}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Conversations sidebar */}
        <AnimatePresence>
          {convsOpen && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setConvsOpen(false)} className="fixed inset-0 bg-black/20 lg:hidden" style={{ zIndex: 39 }} />
              <motion.div initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed left-0 top-0 bottom-0 w-72 bg-white border-r shadow-xl flex flex-col lg:relative lg:shadow-none lg:w-64" style={{ zIndex: 40 }}>
                <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
                  <h3 className="text-sm font-semibold">Conversaciones</h3>
                  <button onClick={() => setConvsOpen(false)} className="p-1 hover:bg-gray-100 rounded lg:hidden">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <button onClick={handleNewConv} className="w-full text-left px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 border-b font-medium">
                    + Nueva conversación
                  </button>
                  {conversations.map(c => (
                    <div key={c.id} className={`flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 cursor-pointer ${activeConvId === c.id ? 'bg-gray-100' : ''}`}>
                      <button onClick={() => handleSelectConv(c.id)} className="flex-1 text-left min-w-0">
                        <p className="text-xs font-medium truncate">{c.title}</p>
                        <p className="text-[10px] text-gray-400">{new Date(c.updatedAt).toLocaleDateString('es-ES')}</p>
                      </button>
                      <button onClick={() => handleDeleteConv(c.id)} className="p-1 text-gray-400 hover:text-red-500 shrink-0 ml-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  ))}
                  {convsLoaded && conversations.length === 0 && (
                    <p className="px-4 py-6 text-xs text-gray-400 text-center">Sin conversaciones anteriores</p>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5" data-chat-scroll>
            {messages.map((msg) => {
              const role = msg.role as string;
              const text = getTextContent(msg);
              if (!text && role === 'user') return null;
              const isUser = role === 'user';
              return (
                <motion.div key={msg.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
                    isUser ? 'bg-black text-white rounded-br-sm' : 'bg-white text-gray-900 rounded-bl-sm shadow-sm border border-gray-100'}`}>
                    {isUser ? <p className="whitespace-pre-wrap">{text}</p> : (
                      <>{text && <div dangerouslySetInnerHTML={{ __html: md(text) }} />}{renderToolParts(msg)}</>
                    )}
                  </div>
                </motion.div>
              );
            })}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            {error && <div className="text-xs text-red-600 bg-red-50 rounded-lg p-2.5">{error.message || 'Error'}</div>}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          <div className="px-4 pb-1 flex gap-1.5 flex-wrap">
            {['Mis pedidos', 'Lo mismo que la última vez', 'Ver carrito'].map(s => (
              <button key={s} type="button" onClick={() => { if (!isLoading) sendMessage({ text: s }); }}
                disabled={isLoading}
                className="text-[11px] px-2.5 py-1 rounded-full border border-gray-200 text-gray-500 hover:bg-white hover:text-gray-700 disabled:opacity-40 transition-colors">
                {s}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="px-4 py-3 bg-white border-t relative">
            {/* AI consent inline notice */}
            <AnimatePresence>
              {showDisclaimer && !hasAcceptedAi && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="absolute left-0 right-0 -top-2 -translate-y-full px-4"
                  style={{ zIndex: 50 }}
                >
                  <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-xl shadow-lg p-3 flex items-start gap-2.5 text-xs">
                    <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-700 leading-relaxed">
                        Estás interactuando con una <strong>IA</strong> que puede cometer errores. Revisa siempre el carrito antes de finalizar. Procesamos tu mensaje, nombre, rol y tarifa.{' '}
                        <Link to="/ai-disclosure" className="text-blue-600 hover:underline">Más info</Link>.
                      </p>
                    </div>
                    <button
                      onClick={acceptDisclaimer}
                      className="px-3 py-1 bg-black text-white rounded-md text-[11px] font-medium hover:bg-gray-800 shrink-0"
                    >
                      Entendido
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="flex gap-2 items-end max-w-3xl mx-auto">
              <textarea ref={inputRef} value={inputValue}
                onChange={(e) => setInputValue(e.target.value)} onKeyDown={handleKeyDown}
                onFocus={() => { if (!hasAcceptedAi) setShowDisclaimer(true); }}
                placeholder="Escribe lo que necesitas..." rows={1}
                className="flex-1 border rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-gray-400 resize-none max-h-24"
                disabled={isLoading} />
              <button onClick={handleSend} disabled={isLoading || !inputValue.trim()}
                className="p-2.5 bg-black text-white rounded-full hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed shrink-0 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m0 0l-4-4m4 4l-4 4" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Cart panel */}
        <AnimatePresence>
          {cartOpen && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setCartOpen(false)} className="fixed inset-0 bg-black/30 lg:hidden" style={{ zIndex: 41 }} />
              <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed right-0 bottom-0 w-80 sm:w-96 bg-white border-l shadow-xl flex flex-col lg:relative lg:w-80 lg:shadow-none lg:!top-0 top-16"
                style={{ zIndex: 42 }}>
                <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                  <h2 className="text-sm font-semibold">Carrito ({cart.totalItems})</h2>
                  <button onClick={() => setCartOpen(false)} className="p-1 hover:bg-gray-100 rounded lg:hidden">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {cartLoading ? (
                    <div className="animate-pulse space-y-3"><div className="h-10 bg-gray-100 rounded" /><div className="h-10 bg-gray-100 rounded" /></div>
                  ) : cart.items.length === 0 ? (
                    <div className="text-center py-12 text-gray-400"><p className="text-sm">Carrito vacío</p></div>
                  ) : (
                    <AnimatePresence mode="popLayout">
                      {cart.items.map((item, i) => {
                        const isUpd = updatingItem === item.variantSku;
                        return (
                          <motion.div key={`${item.variantSku}-${i}`}
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} layout
                            className={`bg-gray-50 rounded-lg p-3 ${isUpd ? 'opacity-50' : ''}`}>
                            <div className="flex items-start gap-2.5 mb-2">
                              {item.imageUrl && (
                                <button onClick={() => item.productId && setDrawerProductId(item.productId)}
                                  className="w-10 h-10 rounded bg-white border border-gray-100 shrink-0 overflow-hidden">
                                  <img src={item.imageUrl} alt={item.name} className="w-full h-full object-contain p-0.5" />
                                </button>
                              )}
                              <button onClick={() => item.productId && setDrawerProductId(item.productId)}
                                className="text-xs font-medium text-gray-900 leading-tight text-left hover:text-blue-600 hover:underline flex-1 min-w-0">
                                {item.name}
                              </button>
                              <button onClick={() => item.productId && handleRemoveItem(item.productId, item.variantSku)}
                                disabled={isUpd || !item.productId} className="text-gray-400 hover:text-red-500 shrink-0 disabled:opacity-30">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center border rounded bg-white">
                                <button onClick={() => item.productId && handleUpdateQty(item.productId, item.variantSku, item.quantity - 1)}
                                  disabled={isUpd || !item.productId} className="px-2 py-0.5 text-gray-500 hover:bg-gray-50 disabled:opacity-30 text-xs">-</button>
                                <span className="px-2 py-0.5 text-xs font-medium min-w-[1.5rem] text-center">{item.quantity}</span>
                                <button onClick={() => item.productId && handleUpdateQty(item.productId, item.variantSku, item.quantity + 1)}
                                  disabled={isUpd || !item.productId} className="px-2 py-0.5 text-gray-500 hover:bg-gray-50 disabled:opacity-30 text-xs">+</button>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-semibold">{item.lineTotal.toFixed(2)}&euro;</span>
                                <span className="text-[10px] text-gray-400 block">{item.price.toFixed(2)}&euro;/ud</span>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  )}
                </div>
                {cart.items.length > 0 && (
                  <div className="border-t p-4 space-y-3 shrink-0">
                    <motion.div key={cart.subtotal} initial={{ scale: 0.97 }} animate={{ scale: 1 }}>
                      <div className="flex justify-between font-semibold text-base"><span>Total</span><span>{cart.subtotal.toFixed(2)}&euro;</span></div>
                      <p className="text-[10px] text-gray-400 mt-0.5">{cart.totalItems} uds</p>
                    </motion.div>
                    <Link to="/checkout" className="block w-full text-center bg-black text-white py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">Ir al checkout</Link>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Product drawer */}
      <AnimatePresence>
        {drawerProductId && <ProductDrawer productId={drawerProductId} onClose={() => setDrawerProductId(null)} />}
      </AnimatePresence>

    </div>
  );
};

function md(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
    .replace(/((?:<li[^>]*>.*?<\/li>\s*)+)/g, '<ul class="list-disc space-y-0.5 my-1">$1</ul>')
    .replace(/\n/g, '<br />');
}

export default QuickOrderPage;
